import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MasterDataDto } from 'src/app/services/api-client/models/master-data-dto';
import {
  GreybeeRoleRestControllerService,
  MasterDataRestControllerService
} from 'src/app/services/api-client/services';
import { environment } from 'src/environments/environment';
import { CvDto, CvImageDto } from 'src/app/services/api-client/models';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { CvTypeEnum } from 'src/app/shared/enums/cvType.model';
import { Gender } from 'src/app/shared/enums/gender.model';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { DatePipe } from '@angular/common';
import { ParseCvService } from 'src/app/shared/services/parse-cv.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationWindowComponent, ConfirmationWindowComponentData } from 'src/app/shared/components/confirmation-window/confirmation-window.component';
import Quill from 'quill';

@Component({
  selector: 'app-master-data',
  templateUrl: './master-data.component.html',
  styleUrls: ['./master-data.component.scss']
})

export class MasterDataComponent implements OnChanges, OnInit {

  @Input() id: number = null;
  @Input() cvType: CvTypeEnum = null;
  @Input() image: CvImageDto;
  @Input() public masterData: MasterDataDto = {
    birthday: null,
    gender: null,
    id: null,
    lastname: null,
    marritalStatus: null,
    name: null,
    profile: null,
    qualification: null,
    role: null,
    userType: null
  };
  @Output() public masterDataDtoEmmiter = new EventEmitter<MasterDataDto>();
  @Output() public cvParseHandler = new EventEmitter<CvDto>();
  @Output() public masterDataChanged = new EventEmitter<any>();
  @Output() public parseCv = new EventEmitter<boolean>();

  profileForm: FormGroup;

  get profileFormGetter() {
    return this.profileForm.errors;
  }
  bsModalRef: BsModalRef;

  @ViewChild('image', { static: false }) imageElement: ElementRef;

  public cropperImageEle: ElementRef;
  public imgLoading: boolean = true;
  imgContentType: string = '';
  imgData: string;
  imgBase64: string;

  minAllowedDate = new Date(new Date().getFullYear() - environment.minAge, new Date().getMonth(), new Date().getDate());
  roles = [];
  modules = {}

  imageChangedEvent: any = '';
  croppedImage: any = '';
  showImageCropper: boolean = true;
  editMode = false;
  genders: string[] = ['MALE', 'FEMALE', 'DIVERS'];
  marritalStatuses: string[] = ['NOT_SPECIFIED', 'SINGLE', 'MARRIED', 'REGISTERED_PARTNER'];
  userTypes = ['INTERNAL', 'EXTERNAL'];

  constructor(
    private http: HttpClient,
    private modalService: BsModalService,
    private toastrService: ToastrService,
    private masterDataService: MasterDataRestControllerService,
    private roleService: GreybeeRoleRestControllerService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private parseCvService: ParseCvService,
    private dialog: MatDialog
  ) {
    this.modules = {
      'toolbar': [
        [{'list': 'bullet'}],
      ]
    }
  }

  ngOnInit(): void {
    this.roleService.getGreybeeRoles()
      .subscribe((data) => this.roles = data);
    if (!this.masterData) this.initializeFormField();
    this.profileForm.valueChanges.subscribe((change) => this.masterDataChanged.emit(change));
  }

  ngOnChanges(changes: SimpleChanges) {
    let changedData: MasterDataDto = changes.masterData?.currentValue;
    let parsedImage: CvImageDto = changes.image?.currentValue;
    if (changedData) {
      this.initializeFormField();
      this.getMasterData(changedData);
      this.validateAllFormFields(this.profileForm);
    }
    this.loadImage(parsedImage);
  }

  editorInit(quill: Quill){
    quill.formatText(0, quill.getText().length - 1, 'color', '');
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, function(node, delta){
      delta.forEach(e => {
        if(e.attributes){
          e.attributes.color = '';
          e.attributes.background = '';
        }
      });
      return delta;
    });
  }

  private initializeFormField() {
    this.profileForm = this.fb.group({
      image: [],
      name: ['', Validators.compose([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(40),
        Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)
      ])],
      lastname: ['', Validators.compose([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(40),
        Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/)
      ])],
      title: '',
      role: ['', Validators.required],
      qualification: ['', Validators.compose([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(/^(\s+\S+\s*)*(?!\s).*$/) //not allow a string containing only white spaces

      ])],
      profile: ['', Validators.maxLength(500)],
      gender: ['', Validators.required],
      marritalStatus: ['', Validators.required],
      birthday: ['', Validators.required],
      userType: ['', Validators.required],
    });
  }

  private validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  private getMasterData(data: MasterDataDto): void {
    this.masterData = data;
    if (data.id) this.id = data.id;
    this.profileForm.controls.name.setValue(data.name);
    this.profileForm.controls.lastname.setValue(data.lastname);
    this.profileForm.controls.title.setValue(data.title);
    if (data.role) {
      this.profileForm.controls.role.patchValue(data.role.name);
    }
    this.profileForm.controls.gender.setValue(data.gender);
    this.profileForm.controls.marritalStatus.setValue(data.marritalStatus);
    if (new Date(data.birthday) < this.minAllowedDate) {
      this.profileForm.controls.birthday.setValue(data.birthday);
    }
    this.profileForm.controls.userType.setValue(data.userType);
    this.profileForm.controls.qualification.setValue(data.qualification);
    this.profileForm.controls.profile.setValue(data.profile);
  }

  private loadImage(image?: CvImageDto) {
    if (image) {
      this.imgData = 'data:' + image.imageContentType + ';base64,' + image.image;
      this.imgContentType = image.imageContentType;
      this.imgLoading = false;
    } else if (this.id) {
      this.masterDataService.getCvImage({id: this.id}).subscribe((img) => {
        if (img) {
          this.imgData = 'data:' + img.imageContentType + ';base64,' + img.image;
        }
        this.imgLoading = false;
      }, (error) => this.imgLoading = false);
    }
  }

  public splitImageString() {
    var binaryString = this.imgData.split(',')[1];
    var len = binaryString.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }


  get name(): AbstractControl {
    return this.profileForm.get('name');
  }

  get lastname(): AbstractControl {
    return this.profileForm.get('lastname');
  }

  get title(): AbstractControl {
    return this.profileForm.get('title');
  }

  get gender(): AbstractControl {
    return this.profileForm.get('gender');
  }

  get role(): AbstractControl {
    return this.profileForm.get('role');
  }

  get birthday(): AbstractControl {
    return this.profileForm.get('birthday');
  }

  get profile(): AbstractControl {
    return this.profileForm.get('profile');
  }

  get qualification(): AbstractControl {
    return this.profileForm.get('qualification');
  }

  get userType(): AbstractControl {
    return this.profileForm.get('userType');
  }


  onSubmit() {
    const data = this.profileForm.value;
    if (this.profileForm.valid) {
      const newMasterData: MasterDataDto = {...data};
      if (this.masterData?.id === this.id) {
        newMasterData.id = this.masterData.id;
      }
      if (data.role != null) {
        newMasterData.role = {id: this.id, name: data.role};
      }

      const datepipe: DatePipe = new DatePipe('en-US')
      let formattedDate = datepipe.transform(new Date(data.birthday), 'yyyy-MM-dd');
      newMasterData.birthday = formattedDate;

      const fd = new FormData();
      fd.append('masterdata', new Blob([JSON.stringify(newMasterData)], {type: 'application/json'}));
      if (this.imgData) {
        const blob = new Blob([this.splitImageString()], {type: 'image/png'});
        fd.append('file', blob);
      } else this.imgData = null;
      const httpOptions = {
        params: new HttpParams().set('cvId', this.id.toString())
      };
      this.http.post(environment.apiURL + '/api/v1/masterdata', fd, httpOptions).subscribe(
        (response: MasterDataDto) => {
          this.editMode = false;
          this.showImageCropper = false;
          this.croppedImage = '';
          this.imageChangedEvent = '';
          this.masterData = response;
          this.masterDataDtoEmmiter.emit(response);
          this.masterDataChanged.emit(false);

          this.profileForm.markAsPristine();
          this.translate.get('saved_master_data').subscribe(successMessage => {
            this.toastrService.success(successMessage);
          });
        }, (e) => {
          console.log('Error! Failed to save master data', e);
          this.toastrService.error("Stammdaten nicht gespeichert!");
        }
      );
    }
  }

  isGermanFemale(): boolean {
    return this.profileForm.controls.gender.value == Gender.FEMALE && this.translate.currentLang == 'de';
  }

  isConsultantCv(): boolean {
    return this.cvType === CvTypeEnum.CONSULTANT_CV;
  }

  removeImg() {
    this.imgData = null;
    this.imgContentType = null;
    this.imgData = null;
    this.translate.get('remove_img_msg').subscribe((txt) => {
      this.toastrService.warning(txt);
    });
  }

  public showCVParseModal() {
    this.parseCvService.openUploadSideBar();
    const sub = this.parseCvService.parsedCv.subscribe((cv: CvDto) => {
      if (cv) {
        cv.id = this.id;
        if (this.masterData) {
          cv.masterData.id = this.masterData.id;
        }
        this.cvParseHandler.emit(cv);
        sub.unsubscribe();
      }
    })
  }

  fileChangeEvent(event: any): void {
    if (this.masterData) {
      this.showImageCropper = true;
      this.imageChangedEvent = event;
      let file = event.target.files[0];
      if (event.target.files && file) {
        if (file.type === 'image/jpeg' ||
          file.type === 'image/gif' ||
          file.type === 'image/png' ||
          file.type === 'image/jpg') {
          // File size should be smaller than 10MB
          if (file.size > 10485760) {
            this.translate.get('big_file_msg').subscribe((txt) => {
              this.toastrService.error(txt);
            });
          }
        } else {
          this.translate.get('img_unsupported_type').subscribe(txt => {
            this.dialog.open<ConfirmationWindowComponentData>(ConfirmationWindowComponent, {
              width: '370px',
              disableClose: true,
              autoFocus: false,
              position: {top: '20px'},
              data: {
                message: txt,
                hideClose: true
              }
            });
          });
        }
      }
    } else {
      this.translate.get('upload_data_before_img').subscribe(txt => {
        this.dialog.open<ConfirmationWindowComponentData>(ConfirmationWindowComponent, {
          width: '370px',
          disableClose: true,
          autoFocus: false,
          position: {top: '20px'},
          data: {
            message: txt,
            hideClose: true
          }
        });
      });
    }
  }

  imageCropped(croppedEvent: ImageCroppedEvent) {
    this.croppedImage = croppedEvent.base64;
    this.imgData = croppedEvent.base64;
    this.imgContentType = this.imageChangedEvent?.target?.files[0]?.type;
    this.imgLoading = false;
  }

  editImg() {
    this.imageChangedEvent = null;
    this.imgBase64 = this.imgData;
    this.editMode = true;
  }

}
